import { useSelector } from 'react-redux';

/**
 * Developer-level kill switch (build / deploy time).
 * Set `enabled: false` here to permanently remove a sub-feature regardless of server flags.
 */
export const chatSubFeatures = [
   {
      name: 'attachments',
      enabled: true,
      features: {
         media: true,
         camera: true,
         location: false,
         photo: true,
         video: true,
         audio: true,
         contact: false,
         document: true,
      },
   },
   { name: 'audio-recorder', enabled: true },
   {
      name: 'message-action',
      enabled: true,
      features: {
         'message-action-edit': true,
         'message-action-reply': true,
         'message-action-forward': true,
         'message-action-copy': true,
         'message-action-delete': true,
         'message-action-star': true,
         'message-action-info': true,
         'message-action-reaction': false,
      },
   },
   { name: 'group-chat', enabled: true },
   { name: 'typing-indicator', enabled: true },
   { name: 'last-seen', enabled: true },
   { name: 'chat-info', enabled: true },
   { name: 'mention', enabled: true },
   { name: 'invite_new_member', enabled: true },
];

/**
 * Maps each sub-feature name to its corresponding key in `state.features`
 * (populated by SDK.getAvailableFeatures()).
 */
export const SERVER_FEATURE_MAP = {
   media: 'isAttachmentEnabled',
   'audio-recorder': 'isAudioAttachmentEnabled',
   photo: 'isImageAttachmentEnabled',
   video: 'isVideoAttachmentEnabled',
   audio: 'isAudioAttachmentEnabled',
   document: 'isDocumentAttachmentEnabled',
   'group-chat': 'isGroupChatEnabled',
};

/**
 * Helper to resolve a flat feature name (simple or nested child)
 * against dev registry and server flags.
 */
const resolveFeature = (name, serverFeatures, featureDef) => {
   // 1. Hard off in dev registry
   const devFlag = featureDef?.enabled ?? false;
   if (!devFlag) return false;

   // 2. Check server flag
   const serverKey = SERVER_FEATURE_MAP[name];
   if (!serverKey) return devFlag;

   return serverFeatures[serverKey] ?? true;
};

/**
 * Static check for non-React contexts.
 */
export const isEnabled = name => {
   // Check top level
   let featureDef = chatSubFeatures.find(f => f.name === name);
   if (featureDef) return featureDef.enabled ?? !!featureDef.features;

   // Search all nested feature groups
   for (const group of chatSubFeatures) {
      if (group.features && name in group.features) {
         return group.enabled && group.features[name];
      }
   }

   return false;
};

/**
 * React hook — checks BOTH the dev registry AND the server flags from Redux.
 */
export const useChatFeature = name => {
   const serverFeatures = useSelector(state => state.features);
   const featureDef = chatSubFeatures.find(f => f.name === name);

   if (!featureDef) {
      // Check if it's a nested feature requested directly
      for (const group of chatSubFeatures) {
         if (group.features && name in group.features) {
            if (!group.enabled || !group.features[name]) return false;
            const serverKey = SERVER_FEATURE_MAP[name];
            if (!serverKey) return true;
            return serverFeatures[serverKey] ?? true;
         }
      }
      return false;
   }

   // If it has nested features, resolve each one
   if (featureDef.features) {
      if (!featureDef.enabled) return false;
      return Object.fromEntries(
         Object.entries(featureDef.features).map(([key, val]) => {
            if (!val) return [key, false];
            const serverKey = SERVER_FEATURE_MAP[key];
            if (!serverKey) return [key, val];
            return [key, serverFeatures[serverKey] ?? true];
         }),
      );
   }

   return resolveFeature(name, serverFeatures, featureDef);
};

/**
 * Batch version of useChatFeature.
 */
export const useChatFeatures = names => {
   const serverFeatures = useSelector(state => state.features);

   return Object.fromEntries(
      names.map(name => {
         // Check top level first
         let featureDef = chatSubFeatures.find(f => f.name === name);

         // If not top level, search all nested feature groups
         if (!featureDef) {
            for (const group of chatSubFeatures) {
               if (group.features && name in group.features) {
                  const devFlag = group.enabled && group.features[name];
                  if (!devFlag) return [name, false];

                  const serverKey = SERVER_FEATURE_MAP[name];
                  if (!serverKey) return [name, devFlag];
                  return [name, serverFeatures[serverKey] ?? true];
               }
            }
            return [name, false];
         }

         if (featureDef.features) {
            if (!featureDef.enabled) return [name, false];
            const resolvedNested = Object.fromEntries(
               Object.entries(featureDef.features).map(([key, val]) => {
                  if (!val) return [key, false];
                  const serverKey = SERVER_FEATURE_MAP[key];
                  if (!serverKey) return [key, val];
                  return [key, serverFeatures[serverKey] ?? true];
               }),
            );
            return [name, resolvedNested];
         }

         return [name, resolveFeature(name, serverFeatures, featureDef)];
      }),
   );
};
